/**
 * AppleLoginButton — dual-mode Sign in with Apple.
 *
 * WEB (browser): deep-links to the backend-owned OAuth flow at
 *   GET /api/auth/apple/start -> Apple authorize -> form_post callback ->
 *   JWT cookie -> redirect back into the app.
 *
 * NATIVE iOS (Capacitor): presents Apple's system sheet via
 *   @capacitor-community/apple-sign-in (ASAuthorizationController under the
 *   hood), then posts the identity token to POST /api/auth/apple/native
 *   which verifies it against Apple's JWKS and sets the session cookie
 *   in-place. No cross-site redirect inside the WKWebView — this is the
 *   flow App Review expects and it eliminates the blank-screen failure.
 *
 * The button is only rendered if the backend reports Apple is configured
 * for this environment (env vars present + .p8 key on disk).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Apple } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function randomHex(bytes) {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(text) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function isNativeIOS() {
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
}

export default function AppleLoginButton({ label = 'Continue with Apple', next = '/app/planets/p5' }) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get('/auth/apple/config')
      .then(r => { if (alive) setEnabled(!!r.data?.enabled); })
      .catch(() => { if (alive) setEnabled(false); });
    return () => { alive = false; };
  }, []);

  if (!enabled) return null;

  const startNative = async () => {
    setBusy(true);
    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const rawNonce = randomHex(32);
      const result = await SignInWithApple.authorize({
        clientId: 'com.worldvibe.app',
        scopes: 'email name',
        nonce: await sha256Hex(rawNonce),
        state: randomHex(16),
      });
      const r = result?.response || {};
      if (!r.identityToken) throw new Error('Apple did not return an identity token');
      await api.post('/auth/apple/native', {
        identity_token: r.identityToken,
        nonce: rawNonce,
        given_name: r.givenName || null,
        family_name: r.familyName || null,
        email: r.email || null,
      });
      await refresh();
      toast.success('Welcome to WorldVibe ✨');
      api.post('/planets/p5/join').catch(() => {});
      nav(next);
    } catch (e) {
      // Error code 1000 / "ASAuthorizationError Canceled" = user dismissed
      // the sheet — stay silent. Everything else gets a friendly toast.
      const msg = String(e?.message || e || '');
      if (!/cancel/i.test(msg) && e?.code !== 1000) {
        toast.error('Apple sign in failed. Please try again or use email.');
      }
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (await isNativeIOS()) {
      await startNative();
      return;
    }
    const url = `${BACKEND_URL}/api/auth/apple/start?next=${encodeURIComponent(next)}`;
    window.location.href = url;
  };

  return (
    <Button
      type="button"
      onClick={start}
      disabled={busy}
      data-testid="apple-login-btn"
      className="w-full mt-3 bg-black text-white hover:bg-neutral-900 border border-white/15 font-semibold"
    >
      <Apple size={18} className="mr-2" fill="currentColor" />
      {busy ? 'Contacting Apple…' : label}
    </Button>
  );
}
